import React from 'react';

import PropTypes from 'prop-types';
import { Helmet as ReactHelmet } from 'react-helmet';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';

import appSettings from 'wanderu-config';
import {
    localize,
    selectLocale,
} from 'wanderu-i18n';

import favicon from 'wanderu-assets/images/favicon.ico';

import { buildCanonicalLink } from '../../utils';
import HrefLangs from '../HrefLangs/HrefLangs';

const HOMEPAGE_PATH = '/';

function _getStaticAssetsUrl() {
    return appSettings.config && appSettings.config.s3.staticAssetsUrl;
}

function Helmet(props) {
    const {
        location,
        t,
    } = props;

    const {
        search,
        pathname,
    } = location;

    const {
        description = t('simplest-way-to-book-search-carriers-no-fees'),
        title = t('search-cheap-bus-and-train-tickets-wanderu'),
        image,
        locale,
        noReferrer = false,
        noIndex: noIndexProp = false,
        noFollow = false,
        canonicalPath = pathname, // Pages are canonical unless otherwise specified
        supportedLocales,
        structuredData,
        noIndexLocale,
    } = props;

    let children = React.Children.toArray(props.children);
    const isCanonicalUrl = !search && canonicalPath === pathname;
    const canonicalUrl = buildCanonicalLink({
        locale: canonicalPath === HOMEPAGE_PATH
            ? undefined
            : locale,
        path: canonicalPath,
    });

    // Our current SEO strategy is to noindex all non-canonical pages by default. Non-canonical currently means
    // any page that has a query parameter. Currently all "default" versions of our pages don't use query params
    // Note that the noIndexProp represents that indexation status of the canonical version of the page, not
    // necessarily the current version of the page (if it has query parameters, for example)
    const noIndex = isCanonicalUrl
        ? noIndexProp
        : true;

    // The canonical link is a signal to index the canonical page, so we want to omit it when the canonical page is noindexed
    const includeCanonicalLink = !noIndexProp;

    // Hreflangs are signals to index the localized version of the page. When the canonical version of the page is noindexed,
    // all of the localized versions of the page are also noindexed, so we want to omit them in that case. If the canonical
    // page is indexed, we only include hreflangs for locales that are indexable via the noIndexLocale prop
    const includeHrefLangs = !noIndexProp;

    // We can omit the "structured data"/"json-ld" snippets when the current page is noindexed as they are only
    // useful to indexable pages
    const includeStructuredData = !noIndex;

    if (includeHrefLangs) {
        children = [
            ...children,
            ...HrefLangs({
                path: canonicalPath,
                supportedLocales,
                noIndexLocale,
            }),
        ];
    }

    return (
        <>
            <ReactHelmet>
                <meta charSet="utf-8" />
                <title lang={locale}>
                    {title}
                </title>
                <meta
                    content={description}
                    name="description"
                />
                <meta
                    content="width=device-width, initial-scale=1, maximum-scale=1.0, user-scalable=no"
                    name="viewport"
                />
                <meta
                    content={title}
                    name="twitter:title"
                />
                <meta
                    content={description}
                    name="twitter:description"
                />
                <meta
                    content="summary_large_image"
                    name="twitter:card"
                />
                <meta
                    content="@GoWanderu"
                    name="twitter:site"
                />
                <meta
                    content="@GoWanderu"
                    name="twitter:creator"
                />
                {image
                    ? ([
                        <meta
                            content={image.src}
                            key="twitter:image"
                            name="twitter:image"
                        />,
                        <meta
                            content={image.alt || description}
                            key="twitter:image:alt"
                            name="twitter:image:alt"
                        />,
                    ])
                    : ([
                        <meta
                            content={`${_getStaticAssetsUrl()}meta-images/twitter.png`}
                            key="twitter:image"
                            name="twitter:image"
                        />,
                        <meta
                            content={description}
                            key="twitter:image:alt"
                            name="twitter:image:alt"
                        />,
                    ])}
                <meta
                    content="Wanderu App"
                    name="name:iphone"
                />
                <meta
                    content="962532589"
                    name="id:iphone"
                />
                <meta
                    content="Wanderu App"
                    name="name:ipad"
                />
                <meta
                    content="962532589"
                    name="id:ipad"
                />
                <meta
                    content="Wanderu App"
                    name="name:googleplay"
                />
                <meta
                    content="com.wanderu.wanderu"
                    name="id:googleplay"
                />
                <meta
                    content="app-id=962532589"
                    name="apple-itunes-app"
                />
                <meta
                    content="Wanderu"
                    property="og:site_name"
                />
                <meta
                    content="website"
                    property="og:type"
                />
                <meta
                    content={title}
                    property="og:title"
                />
                <meta
                    content={description}
                    property="og:description"
                />
                {image
                    ? ([
                        <meta
                            content={image.src}
                            key="og:image"
                            property="og:image"
                        />,
                        <meta
                            content={`${image.width}`}
                            key="og:image:width"
                            property="og:image:width"
                        />,
                        <meta
                            content={`${image.height}`}
                            key="og:image:height"
                            property="og:image:height"
                        />,
                        <meta
                            content={image.alt || description}
                            key="og:image:alt"
                            name="og:image:alt"
                        />,
                    ])
                    : ([
                        <meta
                            content={`${_getStaticAssetsUrl()}meta-images/facebook.png`}
                            key="og:image"
                            property="og:image"
                        />,
                        <meta
                            content="1200"
                            key="og:image:width"
                            property="og:image:width"
                        />,
                        <meta
                            content="628"
                            key="og:image:height"
                            property="og:image:height"
                        />,
                        <meta
                            content={`${_getStaticAssetsUrl()}meta-images/pinterest.png`}
                            key="og:image-2"
                            property="og:image"
                        />,
                        <meta
                            content="600"
                            key="og:image:width-2"
                            property="og:image:width"
                        />,
                        <meta
                            content="800"
                            key="og:image:height-2"
                            property="og:image:height"
                        />,
                        <meta
                            content={description}
                            key="og:image:alt"
                            name="og:image:alt"
                        />,
                    ])}
                <meta
                    content={canonicalUrl}
                    property="og:url"
                />
                <meta
                    content="209923055757384"
                    property="fb:app_id"
                />
                <meta
                    content="334404229919186"
                    property="fb:page_id"
                />
                <meta
                    content="9hedG5zeitr5ZsXGkbrsc-oXsZf00QbJTMyUodtQFPQ"
                    name="google-site-verification"
                />
                <meta
                    content="telephone=no"
                    name="format-detection"
                />

                {noReferrer && <meta content="no-referrer" name="referrer" />}
                <meta
                    content={`${noIndex
                        ? 'noindex'
                        : 'index'}, ${noFollow
                        ? 'nofollow'
                        : 'follow'}`}
                    name="robots"
                />
                <link
                    href={favicon}
                    rel="shortcut icon"
                />
                <link
                    href="https://plus.google.com/100345008927536756720"
                    rel="publisher"
                />
                {includeCanonicalLink && <link
                    href={canonicalUrl}
                    rel="canonical"
                />}
                {includeStructuredData && structuredData?.map((data, i) => (
                    <script
                        key={i}
                        type="application/ld+json"
                    >
                        {JSON.stringify(data)}
                    </script>
                ))}
            </ReactHelmet>
            <ReactHelmet>
                {children}
            </ReactHelmet>
        </>
    );
}

Helmet.propTypes = {
    canonicalPath: PropTypes.string,
    children: PropTypes.node,
    description: PropTypes.string,
    image: PropTypes.shape({
        src: PropTypes.string.isRequired,
        width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        alt: PropTypes.string,
    }),
    locale: PropTypes.string,
    location: PropTypes.object,
    noReferrer: PropTypes.bool,
    noIndex: PropTypes.bool,
    noIndexLocale: PropTypes.array,
    noFollow: PropTypes.bool,
    structuredData: PropTypes.arrayOf(PropTypes.object.isRequired),
    supportedLocales: PropTypes.array.isRequired,
    t: PropTypes.func.isRequired,
    title: PropTypes.string,
};

function mapStateToProps(state) {
    return {
        locale: selectLocale(state),
    };
}

export {
    Helmet,
};

export default withRouter(connect(mapStateToProps)(localize(Helmet, ['t'], 'baseApp')));
